import React, { useEffect, useRef } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { LanguageProvider } from '../../contexts/LanguageContext';
import { trackPageView } from '../../utils/analytics';
import { hexToVantaColor } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import VantaBackground from '../../components/landing/VantaBackground';
import Header from '../../components/landing/Header';
import SubscriptionForm from '../../components/landing/SubscriptionForm';
import Footer from '../../components/landing/Footer';
import { BreathingCTA } from '../../components/landing/BreathingCTA';
import debug from '../../utils/debug';

const LandingPageContent: React.FC = () => {
  const { config, loading } = useConfig();
  const { t } = useTranslation();
  const vantaRef = useRef<any>(null);

  // CTA action handlers
  const handleLeftCTA = () => {
    const leftConfig = config.siteSettings?.dualCTASettings?.left;
    if (leftConfig?.action.type === 'external_link') {
      const url = leftConfig.action.url;
      const openInNewTab = leftConfig.action.openInNewTab;

      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
    }
  };

  const handleRightCTA = () => {
    const rightConfig = config.siteSettings?.dualCTASettings?.right;
    if (rightConfig?.action.type === 'external_link') {
      const url = rightConfig.action.url;
      const openInNewTab = rightConfig.action.openInNewTab;

      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
    }
  };

  useEffect(() => {
    // Track page view
    trackPageView('/', 'Landing Page');
    
    // Update document title and meta tags
    document.title = config.seoSettings?.metaTitle || '';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && config.seoSettings?.metaDescription) {
      metaDescription.setAttribute('content', config.seoSettings.metaDescription);
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && config.seoSettings?.metaTitle) {
      ogTitle.setAttribute('content', config.seoSettings.metaTitle);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription && config.seoSettings?.metaDescription) {
      ogDescription.setAttribute('content', config.seoSettings.metaDescription);
    }
    
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage && config.seoSettings?.ogImage) {
      ogImage.setAttribute('content', config.seoSettings.ogImage);
    }
    
    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle && config.seoSettings?.metaTitle) {
      twitterTitle.setAttribute('content', config.seoSettings.metaTitle);
    }
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription && config.seoSettings?.metaDescription) {
      twitterDescription.setAttribute('content', config.seoSettings.metaDescription);
    }
    
    const twitterImage = document.querySelector('meta[property="twitter:image"]');
    if (twitterImage && config.seoSettings?.ogImage) {
      twitterImage.setAttribute('content', config.seoSettings.ogImage);
    }
  }, [config]);
  
  // Prepare Vanta settings - memoize to prevent unnecessary recalculations
  const vantaSettings = React.useMemo(() => {
    if (!config || !config.vantaSettings) return null;
    
    // Create copy of settings to modify
    const settings = {...config.vantaSettings};
    
    // Convert background color if needed
    if (typeof settings.backgroundColor === 'string') {
      if (settings.backgroundColor.startsWith('0x')) {
        settings.backgroundColor = parseInt(settings.backgroundColor.substring(2), 16) as any;
      } else if (settings.backgroundColor.startsWith('#')) {
        settings.backgroundColor = hexToVantaColor(settings.backgroundColor) as any;
      }
    }
    
    return settings;
  }, [config]);
  
  if (loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  debug.log('Rendering landing page with translations');
  
  return (
    <div className="relative min-h-screen" style={{
      color: config.siteSettings?.colors?.textColor || '#000000',
      fontFamily: config.siteSettings?.fonts?.bodyFont || 'sans-serif'
    }}>
      {/* Vanta Birds Background */}
      <VantaBackground ref={vantaRef} settings={vantaSettings} />
      
      {/* Content Container */}
      <div className="relative z-10 flex flex-col min-h-screen p-4 md:p-8">
        {/* Header */}
        <Header 
          mainHeading={{
            type: config.siteSettings?.mainHeading?.type || 'text',
            text: t('mainHeading'),
            image: config.siteSettings?.mainHeading?.imageUrl || ''
          }}
          tagline={t('tagline')}
        />
        
        {/* Hero Section - Top 30% */}
        <div className="flex flex-col items-center text-center pt-8 pb-16">
          <div className="max-w-2xl">
            <p className="text-xl mb-6 leading-relaxed">{t('description')}</p>
            <p className="text-lg italic mb-4">{t('callToAction')}</p>
            <p className="text-3xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-4xl font-bold text-white drop-shadow-lg">{t('launchAnnouncement')}</p>
          </div>
        </div>

        {/* Primary CTAs - Center Stage */}
        {config.siteSettings?.dualCTASettings?.enabled && (
          <div className="relative flex-grow min-h-[400px]">
            <BreathingCTA
              side="left"
              onAction={handleLeftCTA}
              particleSystem={vantaRef.current}
              priority="primary"
            />
            <BreathingCTA
              side="right"
              onAction={handleRightCTA}
              particleSystem={vantaRef.current}
              priority="primary"
            />
          </div>
        )}

        {/* Secondary Subscription - Lower Section */}
        <div className="mt-auto mb-12 flex flex-col items-center">
          <SubscriptionForm
            buttonText={t('formElements.submitButton').toString()}
          />
        </div>
        
        {/* Footer */}
        <Footer 
          socialLinks={config.footerSettings?.socialLinks || {}}
          legalInfo={config.footerSettings?.legalInfo || {}}
        />
      </div>
    </div>
  );
};

// Wrap the content in the LanguageProvider
const LandingPage: React.FC = () => {
  return (
    <LanguageProvider>
      <LandingPageContent />
    </LanguageProvider>
  );
};

export default LandingPage;
